import { useState, useEffect, useRef, useCallback } from 'react';
import { Bot, User, Loader2, TrendingUp, DollarSign, BarChart3, Users, PieChart, Copy, Check } from 'lucide-react';
import ToolCallCard from '../shared/ToolCallCard';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { dataEngineApi } from '../../services/dataEngineApi';
import { DataExplorerInput } from './DataExplorerInput';
import { QueryResultTable } from './QueryResultTable';
import { ChartRenderer } from './ChartRenderer';
import { MarkdownTable } from './MarkdownTable';
import { HtmlBlock } from '../shared/HtmlBlock';
import { normalizeMarkdown } from '../../utils/markdownUtils';
import { chatTheme as th } from '../../theme/chat';

const QUICK_ACTIONS = [
  { icon: DollarSign, label: 'P&L Summary', prompt: 'Show me a profit and loss summary for the current year' },
  { icon: TrendingUp, label: 'Revenue Trend', prompt: 'What is the monthly revenue trend for the past 12 months?' },
  { icon: BarChart3, label: 'Top Expenses', prompt: 'What are the top 10 expense categories this year?' },
  { icon: Users, label: 'Top Customers', prompt: 'Who are the top 5 customers by revenue?' },
  { icon: PieChart, label: 'Compare Companies', prompt: 'Compare total revenue and expenses across all selected companies' },
];

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  if (!text) return null;
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };
  return (
    <button onClick={handleCopy} className={`p-1 rounded transition-colors ${th.copyBtn.base}`} title="Copy">
      {copied ? <Check className={`h-3.5 w-3.5 ${th.copyBtn.success}`} /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

export function DataExplorerChat({ threadId: externalThreadId, selectedCompanies, onSelectCompany, onRemoveCompany, onThreadCreated }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [threadId, setThreadId] = useState(externalThreadId);
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [usage, setUsage] = useState(null);
  const messagesEndRef = useRef(null);
  const abortRef = useRef(null);
  const sendingRef = useRef(false);
  const justStreamedThreadRef = useRef(null);

  useEffect(() => {
    dataEngineApi.getModels().then(d => {
      const m = d.models || [];
      setModels(m);
      if (m.length > 0 && !selectedModel) setSelectedModel(m[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setThreadId(externalThreadId);
    if (sendingRef.current) return;
    if (externalThreadId && externalThreadId === justStreamedThreadRef.current) {
      justStreamedThreadRef.current = null;
      return;
    }
    if (externalThreadId) {
      dataEngineApi.getThread(externalThreadId).then(data => {
        if (sendingRef.current) return;
        setMessages((data.messages || []).map(m => {
          const segs = [];
          if (m.toolCalls?.length) m.toolCalls.forEach(tc => segs.push({ type: 'tool_call', tool: tc.tool, status: tc.status || 'done' }));
          if (m.queryResults?.length) m.queryResults.forEach(qr => segs.push({ type: 'table', columns: qr.columns, rows: qr.rows, executionTimeMs: qr.executionTimeMs }));
          if (m.charts?.length) m.charts.forEach(ch => segs.push({ type: 'chart', option: ch }));
          if (m.content) segs.push({ type: 'text', content: m.content });
          return { role: m.role, content: m.content || '', toolCalls: m.toolCalls || [], queryResults: m.queryResults || [], charts: m.charts || [], segments: segs };
        }));
      }).catch(() => { if (!sendingRef.current) setMessages([]); });
    } else {
      setMessages([]);
    }
  }, [externalThreadId]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  const updateLast = (updater) => {
    setMessages(prev => {
      const last = prev[prev.length - 1];
      if (last?.role === 'assistant') return [...prev.slice(0, -1), updater(last)];
      return prev;
    });
  };

  const handleSend = useCallback(async (text, images) => {
    if (sendingRef.current) return;
    sendingRef.current = true;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setUsage(null);
    setMessages(prev => [...prev,
      { role: 'user', content: text, images: images || [], toolCalls: [], queryResults: [], charts: [], segments: [] },
      { role: 'assistant', content: '', toolCalls: [], queryResults: [], charts: [], segments: [] },
    ]);

    const body = {
      message: text,
      user_id: 'admin',
      thread_id: threadId,
      model: selectedModel || undefined,
      context: selectedCompanies.length > 0 ? {
        selected_rf_ids: selectedCompanies.map(c => c.rf_id),
        selected_companies: selectedCompanies.map(c => ({ rf_id: c.rf_id, name: c.company_name, source_type: c.source_type })),
      } : undefined,
      images: images?.length > 0 ? images.map(img => ({ base64: img.base64, mime_type: img.mime_type })) : undefined,
    };

    try {
      const response = await dataEngineApi.streamChat(body, controller.signal);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEvent = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event:')) {
            currentEvent = line.slice(6).trim();
          } else if (line.startsWith('data:') && currentEvent) {
            const eventType = currentEvent;
            currentEvent = null;
            try {
              const data = JSON.parse(line.slice(5).trim());
              switch (eventType) {
                case 'thread_id':
                  setThreadId(data.thread_id);
                  abortRef.current._newThreadId = data.thread_id;
                  break;
                case 'token':
                  updateLast(last => {
                    const segs = [...(last.segments || [])];
                    const lastSeg = segs[segs.length - 1];
                    if (lastSeg?.type === 'text') {
                      segs[segs.length - 1] = { ...lastSeg, content: lastSeg.content + data.content };
                    } else {
                      segs.push({ type: 'text', content: data.content });
                    }
                    return { ...last, content: last.content + data.content, segments: segs };
                  });
                  break;
                case 'tool_call':
                  updateLast(last => {
                    const existing = last.toolCalls || [];
                    if (existing.some(t => t.tool === data.tool && t.status === 'running')) return last;
                    const segs = [...(last.segments || [])];
                    segs.push({ type: 'tool_call', tool: data.tool, status: 'running' });
                    return { ...last, toolCalls: [...existing, { tool: data.tool, status: 'running' }], segments: segs };
                  });
                  break;
                case 'tool_result':
                  updateLast(last => {
                    const newToolCalls = (last.toolCalls || []).map(tc =>
                      tc.tool === data.tool && tc.status === 'running' ? { ...tc, status: 'done' } : tc
                    );
                    const segs = [...(last.segments || [])];
                    for (let i = segs.length - 1; i >= 0; i--) {
                      if (segs[i].type === 'tool_call' && segs[i].tool === data.tool && segs[i].status === 'running') {
                        segs[i] = { ...segs[i], status: 'done' };
                        break;
                      }
                    }
                    if (data.result) {
                      try {
                        const parsed = JSON.parse(data.result);
                        if (parsed.chart) {
                          segs.push({ type: 'chart', option: parsed.chart });
                        } else {
                          const inner = parsed.data || parsed;
                          if (inner.columns && inner.rows && inner.rows.length > 0 && inner.columns.length > 1) {
                            segs.push({ type: 'table', columns: inner.columns, rows: inner.rows, executionTimeMs: inner.execution_time_ms });
                          }
                        }
                      } catch {}
                    }
                    return { ...last, toolCalls: newToolCalls, segments: segs };
                  });
                  break;
                case 'chart':
                  if (data.option) {
                    updateLast(last => {
                      const segs = [...(last.segments || [])];
                      const hasChart = segs.some(s => s.type === 'chart' && JSON.stringify(s.option) === JSON.stringify(data.option));
                      if (!hasChart) segs.push({ type: 'chart', option: data.option });
                      return { ...last, charts: [...(last.charts || []), data.option], segments: segs };
                    });
                  }
                  break;
                case 'html_block':
                  updateLast(last => {
                    const segs = [...(last.segments || [])];
                    segs.push({ type: 'html_block', html: data.data?.html || data.html, id: data.data?.id || data.id });
                    return { ...last, segments: segs };
                  });
                  break;
                case 'spreadsheet_url':
                  updateLast(last => {
                    const segs = [...(last.segments || [])];
                    segs.push({ type: 'spreadsheet_url', url: data.url, spreadsheetId: data.spreadsheet_id });
                    return { ...last, segments: segs };
                  });
                  break;
                case 'usage':
                  setUsage(data);
                  break;
                case 'error':
                  updateLast(last => ({ ...last, content: last.content + `\n\nError: ${data.message}` }));
                  break;
                case 'done':
                  break;
                default:
                  break;
              }
            } catch {}
          } else if (line.trim() === '') {
            currentEvent = null;
          }
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') { sendingRef.current = false; return; }
      updateLast(last => ({ ...last, content: `Error: ${err.message}` }));
    }
    sendingRef.current = false;
    setLoading(false);
    const newTid = abortRef.current?._newThreadId;
    if (newTid) {
      justStreamedThreadRef.current = newTid;
      if (onThreadCreated) onThreadCreated(newTid);
    }
  }, [threadId, selectedModel, selectedCompanies, onThreadCreated]);

  const isEmpty = messages.length === 0;

  const quickActionsBar = (
    <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
      {QUICK_ACTIONS.map((action) => (
        <button
          key={action.label}
          onClick={() => handleSend(action.prompt)}
          disabled={loading}
          className={`flex items-center gap-2 px-4 py-2 text-sm border rounded-full
                     transition-all disabled:opacity-50 whitespace-nowrap flex-shrink-0
                     ${th.quickAction.text} ${th.quickAction.border} ${th.quickAction.hover}`}
        >
          <action.icon className="h-4 w-4" />
          {action.label}
        </button>
      ))}
    </div>
  );

  const inputBar = (
    <DataExplorerInput
      onSend={handleSend}
      loading={loading}
      models={models}
      selectedModel={selectedModel}
      onModelChange={setSelectedModel}
      selectedCompanies={selectedCompanies}
      onSelectCompany={onSelectCompany}
      onRemoveCompany={onRemoveCompany}
    />
  );

  if (isEmpty) {
    return (
      <div className={`flex-1 flex flex-col ${th.pageBg} overflow-hidden`}>
        <div className="flex-1 flex flex-col items-center justify-center px-4">
          <div className="mb-8 text-center">
            <h1 className={`text-4xl font-light tracking-tight ${th.greeting.text}`}>
              <span className={th.greeting.accent}>&#10038;</span>{' '}
              {getGreeting()}
            </h1>
          </div>
          <div className="w-full max-w-3xl mx-auto">
            {inputBar}
            <div className="px-3 mt-3">
              {quickActionsBar}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex-1 flex flex-col ${th.pageBg} overflow-hidden`}>
      <div className="flex-1 basis-0 overflow-y-auto px-4 py-4">
        <div className="max-w-3xl mx-auto space-y-6 w-full">
          {messages.map((msg, i) => (
            <div key={i} className={`group relative flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}>
              {msg.role === 'assistant' && (
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${th.botIcon}`}>
                  <Bot className="h-4 w-4" />
                </div>
              )}
              <div className={`${msg.role === 'user'
                ? `max-w-[85%] ${th.userBubble}`
                : `flex-1 min-w-0 ${th.assistantText}`}`}>
                {/* Render segments in chronological order */}
                {msg.role === 'assistant' && (msg.segments || []).map((seg, j) => {
                  if (seg.type === 'tool_call') {
                    return <ToolCallCard key={j} tool={seg.tool} status={seg.status} result={seg.result} />;
                  }
                  if (seg.type === 'table') {
                    return (
                      <div key={j} className="my-4">
                        <QueryResultTable columns={seg.columns} rows={seg.rows} executionTimeMs={seg.executionTimeMs} />
                      </div>
                    );
                  }
                  if (seg.type === 'chart') {
                    return (
                      <div key={j} className="my-4">
                        <ChartRenderer option={seg.option} />
                      </div>
                    );
                  }
                  if (seg.type === 'html_block' && seg.html) {
                    return (
                      <div key={j} className="my-3">
                        <HtmlBlock html={seg.html} id={seg.id} onFormSubmit={(text) => handleSend(text)} />
                      </div>
                    );
                  }
                  if (seg.type === 'spreadsheet_url' && seg.url) {
                    return (
                      <div key={j} className="my-3 flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
                        <svg className="h-4 w-4 text-emerald-600 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                        <a href={seg.url} target="_blank" rel="noopener noreferrer"
                          className="text-sm text-emerald-700 hover:text-emerald-900 font-medium underline underline-offset-2">
                          Open Google Sheet
                        </a>
                      </div>
                    );
                  }
                  if (seg.type === 'text' && seg.content) {
                    return (
                      <div key={j} className={`my-3 ${th.prose}`}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ table: MarkdownTable }}>
                          {normalizeMarkdown(seg.content)}
                        </ReactMarkdown>
                      </div>
                    );
                  }
                  return null;
                })}
                {/* User message content */}
                {msg.role === 'user' && (
                  <div>
                    {msg.images?.length > 0 && (
                      <div className="flex gap-2 mb-2 flex-wrap">
                        {msg.images.map((img, idx) => (
                          <img key={idx} src={`data:${img.mime_type};base64,${img.base64}`}
                               alt="attached" className={`max-w-[120px] max-h-[80px] rounded-md border ${th.imageBorder}`} />
                        ))}
                      </div>
                    )}
                    {msg.content}
                  </div>
                )}
                {msg.role === 'assistant' && loading && i === messages.length - 1 && !(msg.segments?.length) && (
                  <div className="flex items-center gap-2 h-7">
                    <Loader2 className={`h-4 w-4 animate-spin ${th.spinner}`} />
                    <span className={`text-sm ${th.thinkingText}`}>Thinking...</span>
                  </div>
                )}
              </div>
              {msg.content && (
                <div className={`opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 self-end mb-1 ${msg.role === 'user' ? 'order-first' : ''}`}>
                  <CopyButton text={msg.content} />
                </div>
              )}
              {msg.role === 'user' && (
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${th.userIcon}`}>
                  <User className="h-4 w-4" />
                </div>
              )}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>
      {usage && (
        <div className={`text-xs text-center py-1 ${th.usageText}`}>
          {(usage.input_tokens + usage.output_tokens).toLocaleString()} tokens
        </div>
      )}
      <div className={`${th.bottomBar.border} ${th.bottomBar.bg}`}>
        {inputBar}
        <div className="max-w-3xl mx-auto px-3 pb-2">
          {quickActionsBar}
        </div>
      </div>
    </div>
  );
}
